import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Book } from '../lib/nocodb';
import { EnhancedWritingModal } from './EnhancedWritingModal';
import { OpenLibraryService, BookSearchResult } from '../services/openLibrary';

interface EnhancedBookFilterProps {
  books: Book[];
  selectedBook: Book | null;
  searchQuery: string;
  onBookSelect: (book: Book | null) => void;
  onSearchChange: (query: string) => void;
  onAddBook: (title: string, author: string, coverUrl?: string, publicationYear?: number) => Promise<Book>;
  onAddLog: (bookId: number, notes: string) => Promise<void>;
  onDeleteBook: (bookId: number) => Promise<void>;
  triggerAddLog?: boolean;
  onTriggerAddLogHandled?: () => void;
}

export function EnhancedBookFilter({
  books,
  selectedBook,
  searchQuery,
  onBookSelect,
  onSearchChange,
  onAddBook,
  onAddLog,
  onDeleteBook,
  triggerAddLog,
  onTriggerAddLogHandled
}: EnhancedBookFilterProps) {
  const [bookSearchQuery, setBookSearchQuery] = useState('');
  const [isBookSearchFocused, setIsBookSearchFocused] = useState(false);
  const [isAddingBook, setIsAddingBook] = useState(false);
  const [newBookTitle, setNewBookTitle] = useState('');
  const [newBookAuthor, setNewBookAuthor] = useState('');
  const [isAddingLog, setIsAddingLog] = useState(false);
  const [newLogNotes, setNewLogNotes] = useState('');
  const [deleteBookConfirm, setDeleteBookConfirm] = useState<{ book: Book; confirmText: string } | null>(null);
  
  // Open Library API integration
  const [apiSearchResults, setApiSearchResults] = useState<BookSearchResult[]>([]);
  const [isSearchingApi, setIsSearchingApi] = useState(false);
  const [selectedApiBook, setSelectedApiBook] = useState<BookSearchResult | null>(null);

  // Get the 3 most recent books (by Id, assuming higher Id = more recent)
  const recentBooks = useMemo(() => {
    return [...books]
      .sort((a, b) => b.Id - a.Id)
      .slice(0, 3);
  }, [books]);

  // Filter books based on search query
  const filteredBooks = useMemo(() => {
    if (!bookSearchQuery.trim()) return books;
    const query = bookSearchQuery.toLowerCase();
    return books.filter(book => 
      book.title.toLowerCase().includes(query) || 
      book.author.toLowerCase().includes(query)
    );
  }, [books, bookSearchQuery]);

  // Debounced API search function
  const debouncedApiSearch = useCallback(
    OpenLibraryService.debounce(async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setApiSearchResults([]);
        return;
      }

      setIsSearchingApi(true);
      try {
        const results = await OpenLibraryService.searchBooks(query, 5);
        setApiSearchResults(results);
      } catch (error) {
        console.error('API search failed:', error);
        setApiSearchResults([]);
      } finally {
        setIsSearchingApi(false);
      }
    }, 500),
    []
  );

  // Trigger API search when book search query changes
  useEffect(() => {
    if (bookSearchQuery.trim() && isBookSearchFocused) {
      debouncedApiSearch(bookSearchQuery);
    } else {
      setApiSearchResults([]);
    }
  }, [bookSearchQuery, isBookSearchFocused, debouncedApiSearch]);

  // Handle external trigger to add log
  useEffect(() => {
    if (triggerAddLog && selectedBook) {
      setIsAddingLog(true);
      onTriggerAddLogHandled?.();
    }
  }, [triggerAddLog, selectedBook, onTriggerAddLogHandled]);

  const handleBookSelect = (bookId: string) => {
    if (bookId === '') {
      onBookSelect(null);
    } else if (bookId === 'add-new') {
      setIsAddingBook(true);
      setNewBookTitle(bookSearchQuery);
    } else if (bookId.startsWith('api-')) {
      // Handle API book selection
      const apiBookIndex = parseInt(bookId.replace('api-', ''));
      const apiBook = apiSearchResults[apiBookIndex];
      if (apiBook) {
        setSelectedApiBook(apiBook);
        setIsAddingBook(true);
        setNewBookTitle(apiBook.title);
        setNewBookAuthor(apiBook.author);
      }
    } else {
      const book = books.find(b => b.Id === parseInt(bookId));
      onBookSelect(book || null);
    }
    setBookSearchQuery('');
    setIsBookSearchFocused(false);
    setApiSearchResults([]);
  };

  const handleAddBook = async () => {
    if (newBookTitle.trim() && newBookAuthor.trim()) {
      try {
        const newBook = await onAddBook(
          newBookTitle.trim(), 
          newBookAuthor.trim(),
          selectedApiBook?.coverUrl,
          selectedApiBook?.year
        );
        onBookSelect(newBook);
        setIsAddingBook(false);
        setNewBookTitle('');
        setNewBookAuthor('');
        setSelectedApiBook(null);
      } catch (error) {
        console.error('Failed to add book:', error);
      }
    }
  };

  const handleAddLog = async () => {
    if (selectedBook && newLogNotes.trim()) {
      try {
        await onAddLog(selectedBook.Id, newLogNotes.trim());
        setIsAddingLog(false);
        setNewLogNotes('');
      } catch (error) {
        console.error('Failed to add log:', error);
      }
    }
  };

  const handleDeleteBook = async () => {
    if (deleteBookConfirm && deleteBookConfirm.confirmText === deleteBookConfirm.book.title) {
      try {
        await onDeleteBook(deleteBookConfirm.book.Id);
        setDeleteBookConfirm(null);
      } catch (error) {
        console.error('Failed to delete book:', error);
      }
    }
  };

  return (
    <>
      <div className="p-3 bg-white rounded-lg shadow-sm space-y-3">
        {/* Row 1: Search inputs */}
        <div className="grid grid-cols-2 gap-3">
          {/* Enhanced Book Filter with Search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Select Book
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search books or add new..."
                value={bookSearchQuery}
                onChange={(e) => setBookSearchQuery(e.target.value)}
                onFocus={() => setIsBookSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsBookSearchFocused(false), 150)} // Delay to allow clicks
                className="w-full px-3 py-2 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              
              {/* Dropdown */}
              {(bookSearchQuery || isBookSearchFocused) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {/* All Books option */}
                  <button
                    onClick={() => handleBookSelect('')}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
                  >
                    <span className="font-medium">All Books</span>
                  </button>
                  
                  {/* Show recent books when focused but no search query */}
                  {!bookSearchQuery && isBookSearchFocused && recentBooks.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                        Recent Books
                      </div>
                      {recentBooks.map((book) => (
                        <button
                          key={book.Id}
                          onClick={() => handleBookSelect(book.Id.toString())}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b"
                        >
                          <div>
                            <div className="font-medium">{book.title}</div>
                            <div className="text-sm text-gray-500">by {book.author}</div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* Filtered local books when typing */}
                  {bookSearchQuery && filteredBooks.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                        Your Books
                      </div>
                      {filteredBooks.map((book) => (
                        <button
                          key={book.Id}
                          onClick={() => handleBookSelect(book.Id.toString())}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
                        >
                          <div>
                            <div className="font-medium">{book.title}</div>
                            <div className="text-sm text-gray-500">by {book.author}</div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}

                  {/* API search results */}
                  {bookSearchQuery && (isSearchingApi || apiSearchResults.length > 0) && (
                    <>
                      <div className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border-b flex items-center gap-2">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {isSearchingApi ? 'Searching online...' : 'Books from Open Library'}
                      </div>
                      {isSearchingApi && (
                        <div className="px-3 py-3 text-center">
                          <svg className="animate-spin h-4 w-4 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                      {apiSearchResults.map((apiBook, index) => (
                        <button
                          key={`api-${index}`}
                          onClick={() => handleBookSelect(`api-${index}`)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b"
                        >
                          <div className="flex items-start gap-3">
                            {apiBook.coverUrl && (
                              <img
                                src={apiBook.coverUrl}
                                alt={`${apiBook.title} cover`}
                                className="w-8 h-12 object-cover rounded border bg-gray-100"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-blue-900 truncate">{apiBook.title}</div>
                              <div className="text-sm text-blue-600">by {apiBook.author}</div>
                              {apiBook.year && (
                                <div className="text-xs text-gray-500">{apiBook.year}</div>
                              )}
                            </div>
                            <div className="text-xs text-blue-500 font-medium">
                              Add to library
                            </div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* Add new book option */}
                  {bookSearchQuery.trim() && !filteredBooks.some(b => 
                    b.title.toLowerCase() === bookSearchQuery.toLowerCase()
                  ) && (
                    <button
                      onClick={() => handleBookSelect('add-new')}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-600 border-t-2 border-blue-200"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add "{bookSearchQuery}" as new book
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Search Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Search Notes
            </label>
            <input
              type="text"
              placeholder={selectedBook ? `Search in "${selectedBook.title}"...` : "Search all notes..."}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Row 2: Selected book display and actions */}
        {selectedBook && !bookSearchQuery && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="px-3 py-1 bg-blue-50 rounded text-sm text-blue-800 flex items-center gap-2">
                <div>
                  <span className="font-medium">{selectedBook.title}</span>
                  <span className="text-blue-600"> by {selectedBook.author}</span>
                </div>
                <button
                  onClick={() => onBookSelect(null)}
                  className="text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded p-1"
                  title="Unselect book and show all reading logs"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clear All button when no book selected but search active */}
        {!selectedBook && searchQuery && (
          <div className="flex justify-end">
            <button
              onClick={() => onSearchChange('')}
              className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* Add Book Modal */}
      {isAddingBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {selectedApiBook ? 'Add Book to Your Library' : 'Add New Book'}
            </h3>
            
            {/* Show API book details if selected */}
            {selectedApiBook && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  {selectedApiBook.coverUrl && (
                    <img
                      src={selectedApiBook.coverUrl}
                      alt={`${selectedApiBook.title} cover`}
                      className="w-12 h-18 object-cover rounded border bg-gray-100"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="text-sm text-blue-600 font-medium mb-1">From Open Library</div>
                    <div className="font-semibold text-gray-900">{selectedApiBook.title}</div>
                    <div className="text-gray-600">by {selectedApiBook.author}</div>
                    {selectedApiBook.year && (
                      <div className="text-sm text-gray-500">Published: {selectedApiBook.year}</div>
                    )}
                    {selectedApiBook.publisher && (
                      <div className="text-sm text-gray-500">Publisher: {selectedApiBook.publisher}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Book Title
                </label>
                <input
                  type="text"
                  value={newBookTitle}
                  onChange={(e) => setNewBookTitle(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter book title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Author
                </label>
                <input
                  type="text"
                  value={newBookAuthor}
                  onChange={(e) => setNewBookAuthor(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter author name"
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setIsAddingBook(false);
                  setNewBookTitle('');
                  setNewBookAuthor('');
                  setSelectedApiBook(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBook}
                disabled={!newBookTitle.trim() || !newBookAuthor.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Book
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Writing Interface */}
      {isAddingLog && selectedBook && (
        <EnhancedWritingModal
          isOpen={isAddingLog}
          bookTitle={selectedBook.title}
          notes={newLogNotes}
          onNotesChange={setNewLogNotes}
          onSave={handleAddLog}
          onCancel={() => {
            setIsAddingLog(false);
            setNewLogNotes('');
          }}
        />
      )}

      {/* Delete Book Confirmation Modal */}
      {deleteBookConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-red-900 mb-4">
              Delete Book and All Related Logs
            </h3>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                This will permanently delete the book "{deleteBookConfirm.book.title}" and all associated reading logs. This action cannot be undone.
              </p>
              
              <p className="text-sm font-medium text-gray-700">
                To confirm, please type the book title exactly as shown:
              </p>
              
              <div className="p-2 bg-gray-100 rounded text-center font-mono">
                {deleteBookConfirm.book.title}
              </div>
              
              <input
                type="text"
                value={deleteBookConfirm.confirmText}
                onChange={(e) => setDeleteBookConfirm({
                  ...deleteBookConfirm,
                  confirmText: e.target.value
                })}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-red-500"
                placeholder="Type the book title here"
              />
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setDeleteBookConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBook}
                disabled={deleteBookConfirm.confirmText !== deleteBookConfirm.book.title}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Book
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}